import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import { IntegrationSourceHelper } from 'app/modules/shared/classes/client-data';
import {
    AccountDetails,
    ClientIdentifier,
    ClientInformationInterface,
    ClientInformationSources,
} from 'app/modules/shared/interfaces/client-data';
import { ClientsService } from 'app/modules/shared/services/clients.service';
import { ToastService } from 'app/modules/shared/services/toast.service';
import { IClientIntegrationParameters } from '../../interfaces/client-integration';
import { ClientIntegrationType } from '../../interfaces/client-integration-type';
import { ContactModel } from '../../interfaces/company-account-data';
import { ClientIntegrationTypeService } from '../../services/client-integration-type.service';
import { ClientIntegrationService } from '../../services/client-integration.service';

@Component({
    selector: 'client-edit-screen',
    templateUrl: './client-edit-screen.component.html',
    styleUrls: ['./client-edit-screen.component.scss'],
})
export class ClientEditScreenComponent implements OnInit {
    @Input() action: 'View' | 'Add' | 'Edit';
    @Input() clientModel: ClientInformationInterface;
    @Output() updatePage = new EventEmitter(); // pass whether it updated or not

    clientInfoSources: ClientInformationSources;
    @ViewChild('upsertTabBar') upsertTabBar: MatTabGroup;
    tabActiveIndex: number = 0;
    form: UntypedFormGroup;
    clientKey: number;

    clientIntegrationTypes: ClientIntegrationType[];
    isNVLite: boolean;

    constructor(
        private clientsService: ClientsService,
        private clientIntegrationService: ClientIntegrationService,
        private clientIntegrationTypeService: ClientIntegrationTypeService,
        private toastService: ToastService,
        private formBuilder: UntypedFormBuilder
    ) {}

    ngOnInit() {
        this.form = this.formBuilder.group({});
        this.getClientIntegrationTypes();
        if (this.action !== 'Add') {
            this.clientInfoSources = IntegrationSourceHelper.setupSources(this.clientModel); // Populate a helper data model for patching the form controls
            this.getAndSetSuggestedClientKey();
        } else {
            this.isNVLite = false;
        }
    }

    setIsNVBase() {
        if (this.action === 'Add') {
            let is_nvlite = this.form.get('clientDetails').get('is_nvlite').value;
            this.isNVLite = is_nvlite === 'true';
        } else if (this.clientModel?.clientDetails) {
            this.isNVLite = this.clientModel.clientDetails.nvLite;
        } else {
            this.isNVLite = false;
        }
    }

    buildDetailsGroup(group: UntypedFormGroup) {
        this.form.addControl('clientDetails', group);
        this.setIsNVBase();
    }

    buildContactsGroup(group: UntypedFormGroup) {
        this.form.addControl('contacts', group);
    }

    buildIdentifiersGroup(group: UntypedFormGroup) {
        this.form.addControl('identifiers', group);
    }

    buildIntegrationsGroup(group: UntypedFormGroup) {
        this.form.addControl('integrations', group);
    }

    closeForm() {
        this.form.reset();
        this.sendResultToParent(false);
    }

    // TODO: Break this out into more functions
    onSubmit() {
        this.form.markAsPristine();
        let formData = this.form.getRawValue();
        let clientDetails = formData.clientDetails;

        let useSSOValue = this.form.get('clientDetails').get('useSSO').value;
        let useHourlyKPIValue = this.form.get('clientDetails').get('use_Hourly_KPI').value;

        clientDetails.useSSO =
            useSSOValue === 'Both' ? null : useSSOValue === 'true' ? true : false;
        clientDetails.use_Hourly_KPI = useHourlyKPIValue === 'false' ? false : true;
        clientDetails.clientSubscriptionTypeKey = +clientDetails.clientSubscriptionTypeKey;

        clientDetails.nvLite = clientDetails.is_nvlite == 'true' ? true : false;

        let loanSeedDateValue = this.form.get('clientDetails').get('loanSeedDate').value;
        clientDetails.isActive = this.form.get('clientDetails').get('isActive').value;
        clientDetails.isLoginDisabled = this.form.get('clientDetails').get('isLoginDisabled').value;

        this.formatClientDates(formData);
        formData.clientDetails.loanSeedDate = loanSeedDateValue;

        if (clientDetails && clientDetails.clientKey) {
            clientDetails.clientKey = parseInt(clientDetails.clientKey);
        }
        let primaryEmailValue = this.form.get('contacts').get('primaryEmail').value;
        let technicalEmailValue = this.form.get('contacts').get('technicalEmail').value;
        let idNMLSValue = this.form.get('identifiers').get('idNMLS').value;
        let idAnalytics = this.form.get('identifiers').get('idAnalytics').value;
        let idHmdaLEIValue = this.form.get('identifiers').get('idHmdaLEI').value;

        let employeeIdentifiersArray = [];
        let employeeIdentifiers = this.form.get('identifiers').get('employeeIdentifiers')[
            'controls'
        ];
        employeeIdentifiers.forEach((employeeIdentifier) => {
            let patchedIdentifier = {
                identifierDescription: employeeIdentifier.controls.identifierDescription.value,
                identifierType: employeeIdentifier.controls.identifierType.value,
                integrationSourceDesc: employeeIdentifier.controls.integrationSourceDesc.value,
                isActive: employeeIdentifier.controls.isActive.value,
                isPrimaryForEmployeeIdentifier:
                    employeeIdentifier.controls.isPrimaryForEmployeeIdentifier.value,
                clientIntegrationSourceKey:
                    employeeIdentifier.controls.clientIntegrationSourceKey.value,
            };
            employeeIdentifiersArray.push(patchedIdentifier);
        });

        // Format a data object with the values from the form
        let savedClient: ClientInformationInterface = {
            action: this.action,
            clientDetails: clientDetails,
            contacts: this.formatContacts(primaryEmailValue, technicalEmailValue),
            identifiers: this.formatIdentities(idNMLSValue, idHmdaLEIValue, idAnalytics),
            clientIntegrationSources: this.clientModel.clientIntegrationSources,
            employeeIdentifiers: employeeIdentifiersArray, // TODO: Add identifiers here
        };

        this.clientsService.upsertClient(savedClient).subscribe(
            () => {
                this.uploadIntegrations(clientDetails);
                this.toastService.success('Client saved.');
                this.sendResultToParent(true);
            },
            (response) => {
                this.toastService.warning('Problem saving client.');
                throw new Error(response);
            }
        );
    }

    uploadIntegrations(clientDetails: AccountDetails) {
        let arrayControls = this.form
            .get('integrations')
            .get('integrationSettings')
            .get('integrationsArray')['controls'];

        let currentIntegrations = [];
        arrayControls.forEach((control) => {
            let integrationType = control.controls.clientIntegrationType.value;
            let ingestSelect = control.controls.ingestSelect.value;

            let patchedControl = {
                clientIntegrationKey: control.controls.clientIntegrationKey.value,
                clientIntegrationType: integrationType,
                ingestSelect: control.controls.ingestSelect.value,
                csExportSelect: control.controls.csExportSelect.value,
                xmlExistingSelect: control.controls.xmlExistingSelect.value,
                zeroPaddedSelect: control.controls.zeroPaddedSelect.value,
                zeroPaddedLen: control.controls.zeroPaddedLen.value,
                integrationOption: ingestSelect,
            };
            currentIntegrations.push(patchedControl);
        });
        this.assignIntegrationTypeKeys(currentIntegrations);
        let parameters: IClientIntegrationParameters;
        currentIntegrations.forEach((integration) => {
            parameters = {
                action: this.action,
                clientKey: clientDetails.clientKey.toString(),
                integrationTypeKey: integration.integrationTypeKey.toString(),
                clientIntegrationKey: integration.clientIntegrationKey?.toString(),
                integrationActive: integration.ingestSelect,
                zeroPaddedIdentifier: integration.zeroPaddedSelect,
                zeroPaddedLen: integration.zeroPaddedLen
                    ? integration.zeroPaddedLen.toString()
                    : '',
                new_Records_Only: integration.xmlExistingSelect,
                compensafeExport: integration.csExportSelect,
                integrationOption: integration.integrationOption,
            };
            this.clientIntegrationService.upsertClientIntegration(parameters).subscribe(
                (data) => {},
                (error) => {
                    throw new Error(error);
                }
            );
        });
    }

    /* Converts type names to type keys on client integrations */
    assignIntegrationTypeKeys(currentIntegrations: any[]) {
        for (let integration of currentIntegrations) {
            let integrationType = this.clientIntegrationTypes.find(
                (type) => type.integrationType == integration.clientIntegrationType
            );
            integration.integrationTypeKey = integrationType.clientIntegrationTypeKey;
        }
    }

    sendResultToParent(value: boolean) {
        this.updatePage.emit({ result: value });
    }

    formatContacts(primary: string, techAdmin: string): ContactModel[] {
        const data: ContactModel[] = [];
        data.push({
            contactTypeAbbr: 'Primary',
            contactType: 'Primary Contact',
            contactValue: primary,
        });
        data.push({
            contactTypeAbbr: 'Technical',
            contactType: 'Technical Admin',
            contactValue: techAdmin,
        });
        return data;
    }

    formatIdentities(nmls: string, lei: string, idAnalytics): ClientIdentifier[] {
        const data: ClientIdentifier[] = [];
        data.push({
            identifierType: 'NMLS',
            identifier: nmls,
        });
        data.push({
            identifierType: 'LEI',
            identifier: lei,
        });
        data.push({
            identifierType: 'IdAnalytics',
            identifier: idAnalytics,
        });
        return data;
    }

    formatClientDates(formValue: any) {
        try {
            formValue.clientDetails.goLiveDate =
                formValue.clientDetails.goLiveDate == '' ||
                formValue.clientDetails.goLiveDate.length < 3
                    ? null
                    : formValue.clientDetails.goLiveDate;

            formValue.clientDetails.loanSeedDate =
                formValue.clientDetails.loanSeedDate == '' ||
                formValue.clientDetails.loanSeedDate < 3
                    ? null
                    : formValue.clientDetails.loanSeedDate;
        } catch (e) {
            formValue.clientDetails.goLiveDate = null;
            formValue.clientDetails.loanSeedDate = null;
        }
    }

    tabChangeHandler(event) {
        this.tabActiveIndex = event.index;
    }

    isActiveTab(index: number) {
        return this.tabActiveIndex === index;
    }

    /* Fetch the next available clientKey & set the form if action is "Add" */
    getAndSetSuggestedClientKey() {
        if (this.action == 'Add') {
            this.clientsService.getSuggestedClientKey().subscribe((res) => {
                this.clientKey = res.suggestedKey;
            });
        }
    }

    getClientIntegrationTypes() {
        this.clientIntegrationTypeService.getClientIntegrationTypes().subscribe(
            (data) => {
                this.clientIntegrationTypes = data;
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    isSaveDisabled() {
        return (
            (this.form.get('clientDetails') && !this.form.get('clientDetails').valid) ||
            (this.form.get('contacts') && !this.form.get('contacts').valid) ||
            (this.form.get('identifiers') && !this.form.get('identifiers').valid) ||
            (this.form.get('integrations') && !this.form.get('integrations').valid)
        );
    }
}
